import type { GameConfig, MatchMode, TeamDraft } from "./types";

export type TournamentFormat = "knockout" | "round-robin";
export type TournamentMatchStatus = "pending" | "finished";

export interface TournamentParticipant {
  id: string;
  name: string;
  players: string[];
  color: string;
}

export interface TournamentMatch {
  id: string;
  round: number;
  order: number;
  participantAId: string;
  participantBId?: string;
  winnerId?: string;
  legsA?: number;
  legsB?: number;
  archivedMatchId?: string;
  status: TournamentMatchStatus;
}

export interface Tournament {
  id: string;
  name: string;
  format: TournamentFormat;
  mode: MatchMode;
  config: GameConfig;
  participants: TournamentParticipant[];
  matches: TournamentMatch[];
  status: "active" | "finished" | "archived";
  createdAt: number;
  updatedAt: number;
}

const colors = ["#f3bc46", "#dd4c43", "#25b8b2", "#6f88ff", "#c86ae8", "#72c963", "#f58a45", "#e46f9d"];

const pairRound = (ids: string[], round: number): TournamentMatch[] => {
  const matches: TournamentMatch[] = [];
  for (let index = 0; index < ids.length; index += 2) {
    const participantAId = ids[index];
    const participantBId = ids[index + 1];
    matches.push({
      id: crypto.randomUUID(),
      round,
      order: index / 2,
      participantAId,
      participantBId,
      winnerId: participantBId ? undefined : participantAId,
      status: participantBId ? "pending" : "finished",
    });
  }
  return matches;
};

export function createTournament(input: {
  name: string;
  format: TournamentFormat;
  mode: MatchMode;
  config: GameConfig;
  drafts: TeamDraft[];
  randomize: boolean;
}): Tournament {
  const participants = input.drafts.map((draft, index) => ({
    id: crypto.randomUUID(),
    name: draft.name.trim(),
    players: draft.players.map((name) => name.trim()).filter(Boolean),
    color: colors[index % colors.length],
  }));
  const ordered = input.randomize
    ? [...participants].sort(() => Math.random() - 0.5)
    : participants;
  const matches = input.format === "knockout"
    ? pairRound(ordered.map((participant) => participant.id), 1)
    : ordered.flatMap((first, firstIndex) =>
        ordered.slice(firstIndex + 1).map((second, secondIndex) => ({
          id: crypto.randomUUID(),
          round: firstIndex + 1,
          order: secondIndex,
          participantAId: first.id,
          participantBId: second.id,
          status: "pending" as const,
        })),
      );
  const now = Date.now();
  return {
    id: crypto.randomUUID(),
    name: input.name.trim() || "SERATA FRECCETTE",
    format: input.format,
    mode: input.mode,
    config: input.config,
    participants,
    matches,
    status: "active",
    createdAt: now,
    updatedAt: now,
  };
}

export function recordTournamentResult(
  tournament: Tournament,
  matchId: string,
  winnerId: string,
  legsA: number,
  legsB: number,
  archivedMatchId: string,
): Tournament {
  if (tournament.matches.find((match) => match.id === matchId)?.status === "finished") return tournament;
  let matches = tournament.matches.map((match) =>
    match.id === matchId
      ? { ...match, winnerId, legsA, legsB, archivedMatchId, status: "finished" as const }
      : match,
  );
  let status = tournament.status;

  if (tournament.format === "knockout") {
    const currentRound = Math.max(...matches.map((match) => match.round));
    const roundMatches = matches.filter((match) => match.round === currentRound);
    if (roundMatches.every((match) => match.status === "finished")) {
      const winners = roundMatches.map((match) => match.winnerId).filter((id): id is string => Boolean(id));
      if (winners.length === 1) status = "finished";
      else matches = [...matches, ...pairRound(winners, currentRound + 1)];
    }
  } else if (matches.every((match) => match.status === "finished")) {
    status = "finished";
  }

  return { ...tournament, matches, status, updatedAt: Date.now() };
}

export function tournamentStandings(tournament: Tournament) {
  return tournament.participants.map((participant) => {
    let played = 0;
    let wins = 0;
    let legsFor = 0;
    let legsAgainst = 0;
    tournament.matches.filter((match) =>
      match.status === "finished" &&
      (match.participantAId === participant.id || match.participantBId === participant.id),
    ).forEach((match) => {
      played += 1;
      if (match.winnerId === participant.id) wins += 1;
      const isA = match.participantAId === participant.id;
      legsFor += isA ? match.legsA ?? 0 : match.legsB ?? 0;
      legsAgainst += isA ? match.legsB ?? 0 : match.legsA ?? 0;
    });
    return { participant, played, wins, losses: played - wins, legsFor, legsAgainst, difference: legsFor - legsAgainst };
  }).sort((a, b) => b.wins - a.wins || b.difference - a.difference || b.legsFor - a.legsFor);
}

export const nextTournamentMatch = (tournament: Tournament) =>
  tournament.matches.find((match) => match.status === "pending" && match.participantBId);
